/**
 * Product-list helpers for Credit Check modal.
 * Mirrors Angular `credit-check-model.component.ts` `displayContacts` / lead resolution.
 */
import { COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import {
    getFinancePartnerCreditStatusList,
    getPrimaryContactRecord,
    isCreditExpiryInTheFuture,
} from './contactCreditStatus';
import type { CreditCheckProductOption } from './CreditCheckModal.types';

const SUPPORTED_PARTNERS = [
    COMMON_CONSTANTS.IGSPartnerName,
    COMMON_CONSTANTS.LightreachPartnerName,
    COMMON_CONSTANTS.SunlightPartnerName,
    COMMON_CONSTANTS.SunnovaPartnerName,
] as const;

const PRODUCT_SORT_ORDER = [
    COMMON_CONSTANTS.SolarProductName,
    COMMON_CONSTANTS.RoofProductName,
    COMMON_CONSTANTS.BatteryProductName,
    COMMON_CONSTANTS.RRProduct,
    COMMON_CONSTANTS.RRRProductName,
] as const;

function productTypeName(p: CreditCheckProductOption): string {
    return String(p.Product_Type_Name ?? p.product_Type_Name ?? '').trim();
}

function partnerDisplayName(p: CreditCheckProductOption): string {
    return String(p.Finance_Partner_Display_Name ?? p.Finance_Partner ?? '').trim();
}

function financeMethodName(p: CreditCheckProductOption): string {
    return String(p.Finance_Method_Name ?? p.Finance_Method ?? '').trim();
}

function matchesProductTypeName(statusProductTypeName: unknown, productName: string): boolean {
    const statusName = String(statusProductTypeName ?? '').trim();
    if (!statusName || !productName) return false;
    return (
        statusName === productName ||
        statusName === `${productName} Only` ||
        `${statusName} Only` === productName
    );
}

/** True when credit status row indicates a passed credit check (exclude from product list). */
function isCreditPassedStatus(statusObj: Record<string, unknown> | undefined): boolean {
    if (!statusObj || !isCreditExpiryInTheFuture(statusObj)) return false;
    if (statusObj.IsCreditPass === true || statusObj.isCreditPass === true) return true;
    const syncStatus = String(
        statusObj.Finance_Partner_Sync_Credit_Status ?? statusObj.finance_Partner_Sync_Credit_Status ?? '',
    )
        .trim()
        .toLowerCase();
    return syncStatus === 'passed';
}

/**
 * Build the Product dropdown list (active, supported partners, not credit-passed,
 * Solar collapses same-partner/method siblings, custom sort).
 */
export function buildCreditCheckProductList(opportunityDetails: any): CreditCheckProductOption[] {
    const raw =
        opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    if (!Array.isArray(raw)) return [];

    let productList: CreditCheckProductOption[] = raw
        .filter((p: any) => p && p.ActiveFlag !== false)
        .map((p: any) => ({
            ...p,
            Product_Type_Id: p.Product_Type_Id ?? p.product_Type_Id,
            Product_Type_Name: productTypeName(p),
            Finance_Partner_Display_Name: partnerDisplayName(p),
            Finance_Method_Name: financeMethodName(p),
        }))
        .filter((p: CreditCheckProductOption) =>
            SUPPORTED_PARTNERS.includes(partnerDisplayName(p) as (typeof SUPPORTED_PARTNERS)[number]),
        );

    const primaryContact = getPrimaryContactRecord(opportunityDetails);
    const creditStatusList = primaryContact ? getFinancePartnerCreditStatusList(primaryContact) : [];

    productList = productList.filter((p) => {
        const name = productTypeName(p);
        const statusObj = creditStatusList.find((s) =>
            matchesProductTypeName(s.ProductTypeName ?? s.productTypeName, name),
        );
        return !isCreditPassedStatus(statusObj);
    });

    const solarProduct = productList.find(
        (p) => productTypeName(p) === COMMON_CONSTANTS.SolarProductName,
    );
    if (solarProduct) {
        const solarPartner = partnerDisplayName(solarProduct);
        const solarMethod = financeMethodName(solarProduct);
        productList = productList.filter((p) => {
            if (productTypeName(p) === COMMON_CONSTANTS.SolarProductName) return true;
            if (partnerDisplayName(p) === solarPartner && financeMethodName(p) === solarMethod) {
                return false;
            }
            return true;
        });
    }

    productList.sort((a, b) => {
        const ai = PRODUCT_SORT_ORDER.indexOf(productTypeName(a) as (typeof PRODUCT_SORT_ORDER)[number]);
        const bi = PRODUCT_SORT_ORDER.indexOf(productTypeName(b) as (typeof PRODUCT_SORT_ORDER)[number]);
        const aIdx = ai === -1 ? PRODUCT_SORT_ORDER.length : ai;
        const bIdx = bi === -1 ? PRODUCT_SORT_ORDER.length : bi;
        return aIdx - bIdx;
    });

    return productList;
}

/** Find credit-status row for a product on the primary contact (product-specific sync contact). */
export function findProductCreditStatus(
    opportunityDetails: any,
    product: CreditCheckProductOption | null | undefined,
): Record<string, unknown> | undefined {
    if (!product) return undefined;
    const primaryContact = getPrimaryContactRecord(opportunityDetails);
    if (!primaryContact) return undefined;
    const creditStatusList = getFinancePartnerCreditStatusList(primaryContact);
    const name = productTypeName(product);
    return creditStatusList.find((s) =>
        matchesProductTypeName(s.ProductTypeName ?? s.productTypeName, name),
    );
}

/**
 * Resolve Finance Partner Sync Lead ID for the selected product
 * (FinancePartnerLeadList → product lead → opportunity lead fallbacks).
 */
export function resolveCreditCheckLeadId(
    opportunityDetails: any,
    product: CreditCheckProductOption | null | undefined,
): string | null {
    if (!product) {
        return (
            opportunityDetails?.Finance_Partner_Sync_LeadID ||
            opportunityDetails?.OpportunityDetails?.Finance_Partner_Sync_LeadID ||
            opportunityDetails?.Finance_Partner_Sync_CO_LeadID ||
            null
        );
    }

    const name = productTypeName(product);
    const financePartnerLeadList =
        opportunityDetails?.FinancePartnerLeadList ??
        opportunityDetails?.financePartnerLeadListModels ??
        opportunityDetails?.financePartnerLeadList ??
        [];

    if (Array.isArray(financePartnerLeadList)) {
        const matchedLead = financePartnerLeadList.find((ele: any) =>
            matchesProductTypeName(ele?.ProductTypeName ?? ele?.productTypeName, name),
        );
        const leadFromList =
            matchedLead?.Finance_Partner_Sync_LeadID ?? matchedLead?.finance_Partner_Sync_LeadID;
        if (leadFromList) return String(leadFromList);
    }

    if (product.Finance_Partner_Sync_LeadID) {
        return String(product.Finance_Partner_Sync_LeadID);
    }

    return (
        opportunityDetails?.Finance_Partner_Sync_LeadID ||
        opportunityDetails?.OpportunityDetails?.Finance_Partner_Sync_LeadID ||
        opportunityDetails?.Finance_Partner_Sync_CO_LeadID ||
        null
    );
}

export function formatCreditCheckProductLabel(product: CreditCheckProductOption): string {
    const name = productTypeName(product) || 'Product';
    const partner = partnerDisplayName(product);
    return partner ? `${name} (${partner})` : name;
}

export function isIgsPartnerName(partnerName: string): boolean {
    return partnerName.trim().toUpperCase().includes('IGS');
}

export function isLightreachOrSunlightPartnerName(partnerName: string): boolean {
    const up = partnerName.trim().toUpperCase();
    return up.includes('LIGHTREACH') || up.includes('SUNLIGHT');
}

export function isSunnovaPartnerName(partnerName: string): boolean {
    return partnerName.trim().toUpperCase().includes('SUNNOVA');
}
